import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import ExternalAuthentication from '@utils/external-authentication'
import Row from 'react-bootstrap/Row'
import User from '@utils/storage/user'
import LOGIN_BY_AUTH_CODE from '@graphql/mutations/login-by-auth-code'
import { toast } from 'react-toastify'
import ToastMessage from '@components/ToastMessage/ToastMessage'
import Spinner from 'react-bootstrap/Spinner'
import { RETURN_URL_KEY, validateReturnUrl } from '@utils/auth/returnUrl'

const DEFAULT_ROUTE = '/applications'

function LoginByAuthCode() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loginByAuthCode, { loading, error }] = useMutation(LOGIN_BY_AUTH_CODE, {
      onCompleted(data) {
        if (!data?.loginByAuthCode) return

        User.set({login: data.loginByAuthCode.login, role: data.loginByAuthCode.role})
        toast(<ToastMessage message='Logged in!' />)

        let targetRoute = DEFAULT_ROUTE

        try {
          const storedUrl = sessionStorage.getItem(RETURN_URL_KEY)

          sessionStorage.removeItem(RETURN_URL_KEY)

          if (storedUrl && validateReturnUrl(storedUrl)) {
            targetRoute = storedUrl
          }
        } catch {
          // Silently fall back to default route
        }

        navigate(targetRoute)
      },
      onError() { toast(<ToastMessage message="Authentication wasn't successful!" variant='danger' />) }
    }
  )

  useEffect(() => {
    const accessCode = searchParams.get('code')
    const codeVerifier = sessionStorage.getItem('codeVerifier')

    if (!accessCode || !codeVerifier || sessionStorage.getItem('pkce_exchanged')) return

    /**
     * One-shot guard for the PKCE code→token exchange in this tab.
     * React 18 StrictMode, quick remounts, HMR, or back/forward/reload can re-run
     * this effect and accidentally fire a second exchange. Setting this flag makes
     * the flow idempotent and prevents duplicate requests (e.g., invalid_grant).
     * Scoped to sessionStorage so it applies only to the current tab and clears on
     * tab close. Remove this flag after a successful exchange (together with
     * 'codeVerifier'), or clear it on error to allow a manual retry.
     * Note: this is not security-sensitive—purely a client-side sentinel.
     */
    sessionStorage.setItem('pkce_exchanged', '1')

    loginByAuthCode({ variables: { accessCode, codeVerifier } })
      .then(() => {
        sessionStorage.removeItem('codeVerifier')
        sessionStorage.removeItem('pkce_exchanged')
      })
      .catch(() => {
        sessionStorage.removeItem('pkce_exchanged')
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      { loading && <Spinner animation='border' size='sm' variant='dark' /> }
      { error && (
        <Container>
          <Card className='mt-3'>
            <Card.Body>
              <Row>
                <Col md={{ span: 6, offset: 3 }}>
                  <Alert variant='danger'>{error.message}</Alert>
                  <p>Authentication wasn't successful! Please try to log in again:</p>
                  <div className='d-grid gap-2'>
                    <Button
                      variant='success'
                      onClick={() => ExternalAuthentication.redirect()}
                    >
                      Log in
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      ) }
    </>
  )
}

export default LoginByAuthCode
